import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiActivity, FiEdit3, FiPlus, FiUsers } from 'react-icons/fi';

import { adminLearningPathsApi } from '@/services/adminLearningPaths';
import { PATHS } from '@/routes/paths';
import { Button, ButtonIcon } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHeaderWrapper, TableNoData, TableRow } from '@/components/ui/table';
import { Filter, FilterContent, FilterRow } from '@/components/custom/filter';
import { Loader } from '@/components/custom/loader';
import { DashboardContent } from '@/layouts/dashboard';

import EditTrilhaModal from './EditTrilhaModal';
import NovaTrilhaModal from './NovaTrilhaModal';
import TrilhaGenerationsModal from './TrilhaGenerationsModal';

const KIND_CONFIG = {
  adaptive: { label: 'Adaptativa', variant: 'info' },
  exam: { label: 'Matriz', variant: 'default' }
};

const TABLE_HEAD = [
  { label: 'Trilha' },
  { label: 'Slug', width: 180 },
  { label: 'Tipo', width: 110 },
  { label: 'Blocos', width: 90 },
  { label: 'Alunos (iniciaram · ativos · concluíram)', width: 260 },
  { label: 'Status', width: 100 },
  { label: 'Ações', width: 200 }
];

function truncate(text = '', n = 90) {
  return text.length > n ? `${text.slice(0, n - 1)}…` : text;
}

export default function TrilhasAdmin() {
  const navigate = useNavigate();
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editTarget, setEditTarget] = useState(null);
  const [progressOpen, setProgressOpen] = useState(false);
  const [lastGenerationId, setLastGenerationId] = useState(null);
  const [novaTrilhaOpen, setNovaTrilhaOpen] = useState(false);

  const breadcrumbs = {
    title: 'Trilhas',
    nav: [{ name: 'Gerenciar trilhas', isActive: true }]
  };

  const load = () => {
    setLoading(true);
    return adminLearningPathsApi
      .index()
      .then(json => {
        const payload = json?.data || [];
        setPaths(payload.map(d => ({ id: d.id, ...d.attributes })));
      })
      .catch(() => {
        toast.error('Erro ao carregar trilhas');
        setPaths([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  // O catálogo de trilhas é pequeno (unidades) — busca client-side, sem paginação.
  const visible = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return paths;
    return paths.filter(p => p.name?.toLowerCase().includes(q) || p.slug?.toLowerCase().includes(q));
  }, [paths, searchTerm]);

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
      breadcrumbsActions={
        <>
          <Button
            variant="outline"
            onClick={() => {
              setLastGenerationId(null);
              setProgressOpen(true);
            }}
          >
            <ButtonIcon>
              <FiActivity />
            </ButtonIcon>{' '}
            Acompanhar gerações
          </Button>
          <Button onClick={() => setNovaTrilhaOpen(true)}>
            <ButtonIcon>
              <FiPlus />
            </ButtonIcon>{' '}
            Nova trilha
          </Button>
        </>
      }
    >
      <Filter variant="plain">
        <FilterContent>
          <FilterRow className="min-w-[280px]">
            <Label htmlFor="trilha-search">Buscar</Label>
            <Input
              id="trilha-search"
              type="search"
              placeholder="Nome ou slug da trilha"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </FilterRow>
        </FilterContent>
      </Filter>

      {loading ? (
        <Loader />
      ) : (
        <Table className="min-w-[1000px]">
          <TableHeaderWrapper tableHead={TABLE_HEAD} />
          <TableBody>
            {visible.map(p => {
              const kind = KIND_CONFIG[p.kind] || { label: p.kind, variant: 'neutral-lightest' };
              return (
                <TableRow key={p.id}>
                  <TableCell>
                    <p className="mb-0 font-medium text-primary-dark">{p.name}</p>
                    {p.description && <p className="mb-0 text-xs text-neutral">{truncate(p.description)}</p>}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-neutral">{p.slug}</TableCell>
                  <TableCell>
                    <Badge variant={kind.variant}>{kind.label}</Badge>
                  </TableCell>
                  <TableCell className="tabular-nums">{p.blocks_available ?? '—'}</TableCell>
                  <TableCell className="tabular-nums">
                    {p.students_started ?? 0} · {p.students_active ?? 0} · {p.students_completed ?? 0}
                  </TableCell>
                  <TableCell>
                    <Badge variant={p.active ? 'success' : 'neutral-lightest'}>{p.active ? 'Ativa' : 'Inativa'}</Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="xs"
                      className="mr-2"
                      onClick={() => setEditTarget(p)}
                    >
                      <ButtonIcon>
                        <FiEdit3 />
                      </ButtonIcon>{' '}
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={() => navigate(PATHS.TRILHAS_ADMIN.STUDENTS(p.id))}
                    >
                      <ButtonIcon>
                        <FiUsers />
                      </ButtonIcon>{' '}
                      Ver alunos
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            <TableNoData
              notFound={visible.length === 0}
              colSpan={TABLE_HEAD.length}
            />
          </TableBody>
        </Table>
      )}

      <EditTrilhaModal
        trilha={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={load}
      />

      <TrilhaGenerationsModal
        open={progressOpen}
        onOpenChange={setProgressOpen}
        highlightId={lastGenerationId}
      />

      <NovaTrilhaModal
        open={novaTrilhaOpen}
        onOpenChange={setNovaTrilhaOpen}
        onSuccess={created => {
          load();
          setLastGenerationId(created?.id || null);
          setProgressOpen(true);
        }}
      />
    </DashboardContent>
  );
}
